import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { Flask, FilePdf, MagnifyingGlass, Plus, Trash, Warning, Archive } from "@phosphor-icons/react";
import CoshhNewDialog from "./CoshhNewDialog";
import { useBrandedPdf } from "./useBrandedPdf";

const PICTO_TONE = { Corrosive: "bg-slate-800 text-white", Flammable: "bg-red-100 text-red-800", "Harmful / irritant": "bg-amber-100 text-amber-800", "Health hazard (sensitiser, carcinogen)": "bg-rose-100 text-rose-800",
  "Acute toxic": "bg-red-200 text-red-900", "Hazardous to the environment": "bg-emerald-100 text-emerald-800", Oxidising: "bg-orange-100 text-orange-800", "Gas under pressure": "bg-sky-100 text-sky-800", Explosive: "bg-red-200 text-red-900" };

export function Pictos({ list = [] }) {
  const shown = list.filter(p => p && p !== "None known");
  if (!shown.length) return <span className="text-[11px] text-muted-foreground">No label hazards</span>;
  return <div className="flex flex-wrap gap-1">{shown.map(p => <span key={p} className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${PICTO_TONE[p] || "bg-muted"}`}>{p.replace(" (sensitiser, carcinogen)", "")}</span>)}</div>;
}

export function DocState({ d, label, testId }) {
  const st = d?.status || "none";
  const cls = st === "drafted" ? "bg-emerald-100 text-emerald-800" : st === "pending" ? "bg-sky-100 text-sky-700 animate-pulse" : st === "failed" ? "bg-red-100 text-red-700" : "bg-muted text-muted-foreground";
  const text = st === "drafted" ? `${label} issued${d.source === "upload" ? " · from SDS" : ""}` : st === "pending" ? `AI writing ${label.toLowerCase()}…` : st === "failed" ? `${label} failed` : `No ${label.toLowerCase()}`;
  return <Badge className={`text-[10px] ${cls}`} data-testid={testId}>{text}</Badge>;
}

function Stat({ label, value, tone = "" }) {
  return <div className={`bg-card border border-border rounded-md p-3 ${tone}`}><p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</p><p className="text-2xl font-[Chivo] font-bold leading-tight mt-0.5">{value}</p></div>;
}

export default function CoshhPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [showNew, setShowNew] = useState(false);
  const { openBranded, brandingDialog } = useBrandedPdf();

  const load = useCallback(() => {
    API.get("/coshh/substances", { params: { search: search || undefined, status } }).then(({ data }) => setData(data)).catch(() => {});
  }, [search, status]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    if (!data?.totals?.generating) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [data, load]);

  const rows = data?.substances || [];
  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.reference} ${r.product} and its documents from the register and Compliance Hub?`)) return;
    try { await API.delete(`/coshh/substances/${r.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const archive = async (r) => {
    try { const { data: d } = await API.post(`/coshh/substances/${r.id}/archive`); toast.success(d.status === "archived" ? "Removed from the active register" : "Restored"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="coshh-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Substances" value={data?.totals?.total ?? "—"} />
        <Stat label="Assessed" value={data ? `${data.totals.assessed}/${data.totals.total}` : "—"} tone={data && data.totals.assessed < data.totals.total ? "border-amber-300" : ""} />
        <Stat label="Data sheets" value={data ? `${data.totals.data_sheets}/${data.totals.total}` : "—"} />
        <Stat label="Review due" value={data?.totals?.review_due ?? "—"} tone={data?.totals?.review_due ? "border-red-300" : ""} />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search product, manufacturer or reference…" className="h-8 pl-8 text-sm" data-testid="coshh-search" />
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="coshh-status">
          <option value="active">Active register</option>
          <option value="archived">Archived</option>
        </select>
        <Button size="sm" variant="outline" onClick={() => openBranded({ url: "/coshh/register.pdf", filename: "COSHH_register.pdf", title: "COSHH register PDF branding" })} data-testid="coshh-register-pdf"><FilePdf size={14} className="mr-1" /> Register PDF</Button>
        <Button size="sm" onClick={() => setShowNew(true)} data-testid="coshh-new-btn"><Plus size={14} className="mr-1" /> Add substance</Button>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left px-3 py-2">Product</th><th className="text-left px-3 py-2 hidden md:table-cell">Hazards</th><th className="text-left px-3 py-2 hidden lg:table-cell">Used</th><th className="text-left px-3 py-2">Documents</th><th className="px-3 py-2" /></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-border hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/health-safety/coshh/${r.id}`)} data-testid={`coshh-row-${r.id}`}>
                <td className="px-3 py-2"><div className="font-medium leading-tight">{r.product}</div><div className="text-[11px] text-muted-foreground">{r.reference}{r.manufacturer ? ` · ${r.manufacturer}` : ""}{r.form ? ` · ${r.form}` : ""}</div></td>
                <td className="px-3 py-2 hidden md:table-cell"><Pictos list={r.hazards?.pictograms || r.pictograms} />{r.hazards?.signal_word && r.hazards.signal_word !== "None" && <span className="text-[10px] font-semibold text-red-700 uppercase">{r.hazards.signal_word}</span>}</td>
                <td className="px-3 py-2 hidden lg:table-cell text-xs text-muted-foreground max-w-[260px] truncate" title={r.use}>{r.where ? <span className="font-medium text-foreground">{r.where} · </span> : null}{r.use}</td>
                <td className="px-3 py-2"><div className="flex flex-col gap-1 items-start"><DocState d={r.assessment} label="Assessment" testId={`coshh-assessment-state-${r.id}`} /><DocState d={r.data_sheet} label="Data sheet" testId={`coshh-datasheet-state-${r.id}`} />{r.review_state === "overdue" && <span className="text-[10px] text-red-700 flex items-center gap-1"><Warning size={11} /> review overdue</span>}</div></td>
                <td className="px-3 py-2 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                  <Button size="icon" variant="ghost" className="h-7 w-7" title={status === "active" ? "Archive" : "Restore"} onClick={() => archive(r)} data-testid={`coshh-archive-${r.id}`}><Archive size={15} /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" title="Delete" onClick={() => remove(r)} data-testid={`coshh-delete-${r.id}`}><Trash size={15} /></Button>
                </td>
              </tr>
            ))}
            {data && !rows.length && <tr><td colSpan={5} className="px-3 py-10 text-center text-sm text-muted-foreground" data-testid="coshh-empty"><Flask size={22} className="mx-auto mb-2 opacity-60" />No substances on the register yet. Add the products your operatives use — the AI writes the COSHH assessment and data sheet in about a minute each.</td></tr>}
          </tbody>
        </table>
      </div>
      <CoshhNewDialog open={showNew} onOpenChange={setShowNew} questions={data?.questions} onCreated={load} />
      {brandingDialog}
    </div>
  );
}
